/**
 * Pure UI state for the site search combobox: the query, whether the list is open and
 * which match is highlighted. Matches themselves are derived (see searchSites), so the
 * reducer only needs their count when moving the highlight.
 */

export interface SiteSearchState {
  readonly query: string;
  readonly open: boolean;
  /** Highlighted match, or null when none is highlighted */
  readonly activeIndex: number | null;
}

export type SiteSearchAction =
  | { readonly type: 'query-changed'; readonly query: string }
  | { readonly type: 'moved'; readonly direction: 'next' | 'previous'; readonly matchCount: number }
  | { readonly type: 'picked' }
  | { readonly type: 'escaped' }
  | { readonly type: 'closed' };

export const INITIAL_SITE_SEARCH_STATE: SiteSearchState = {
  query: '',
  open: false,
  activeIndex: null,
};

/** Initial state for a restored query; the list stays closed until the user interacts. */
export function initialSiteSearchState(query: string): SiteSearchState {
  return { ...INITIAL_SITE_SEARCH_STATE, query };
}

function assertNever(value: never): never {
  throw new Error(`Unexpected action: ${JSON.stringify(value)}`);
}

function close(state: SiteSearchState): SiteSearchState {
  return { ...state, open: false, activeIndex: null };
}

function moveIndex(
  current: number | null,
  direction: 'next' | 'previous',
  matchCount: number
): number {
  const valid = current !== null && current < matchCount ? current : null;
  switch (direction) {
    case 'next':
      return valid === null ? 0 : (valid + 1) % matchCount;
    case 'previous':
      return valid === null ? matchCount - 1 : (valid - 1 + matchCount) % matchCount;
    default:
      return assertNever(direction);
  }
}

export function siteSearchReducer(state: SiteSearchState, action: SiteSearchAction): SiteSearchState {
  switch (action.type) {
    case 'query-changed':
      return { query: action.query, open: true, activeIndex: null };
    case 'moved':
      if (action.matchCount === 0) return state;
      return {
        ...state,
        open: true,
        activeIndex: moveIndex(state.open ? state.activeIndex : null, action.direction, action.matchCount),
      };
    case 'picked':
    case 'closed':
      return close(state);
    case 'escaped':
      return state.open ? close(state) : INITIAL_SITE_SEARCH_STATE;
    default:
      return assertNever(action);
  }
}

/** The highlight to show, guarding against the matches having shrunk since it was set. */
export function effectiveActiveIndex(state: SiteSearchState, matchCount: number): number | null {
  if (!state.open || state.activeIndex === null) return null;
  return state.activeIndex < matchCount ? state.activeIndex : null;
}
